import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import UserMenu from './UserMenu';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useUserRole } from '@/hooks/useUserRole';
import ComprehensiveNotificationBell from './notifications/ComprehensiveNotificationBell';
import { RoleBasedNavigationMenu } from './navigation/RoleBasedNavigationMenu';
import { GameWorkflowAutomation } from './workflow/GameWorkflowAutomation';

const Header = ({ className = '' }: { className?: string }) => {
  const navigate = useNavigate();
  const { user: supabaseUser, loading: supabaseLoading, initialCheckComplete: supabaseInitialCheck } = useAuth();
  const { user: internalUser, loading: internalLoading, initialCheckComplete: internalInitialCheck } = useInternalAuth();
  const { permissions, userId } = useUserRole();
  
  // Wait for both auth systems to complete their initial checks
  const bothSystemsReady = supabaseInitialCheck && internalInitialCheck;
  const anySystemLoading = supabaseLoading || internalLoading;
  // Ensure consistent authentication state - user is authenticated if either system has a user
  // but prioritize single active session to prevent switching
  const authenticatedUser = (internalUser && !supabaseUser) ? internalUser : 
                           (!internalUser && supabaseUser) ? supabaseUser :
                           internalUser ? internalUser : supabaseUser;
  const isAuthenticated = !!authenticatedUser;

  console.log('=== HEADER RENDER DEBUG ===');
  console.log('Supabase user:', supabaseUser?.id || 'none');
  console.log('Internal user:', internalUser?.id || 'none');
  console.log('Both systems ready:', bothSystemsReady);
  console.log('Any system loading:', anySystemLoading);
  console.log('Is authenticated:', isAuthenticated);

  const handleSignInClick = () => {
    navigate('/login', { state: { activeTab: 'login' } });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-black border-b border-gray-800 ${className}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">{/* Increased height from h-16 to h-20 */}
        <Link to="/" className="flex items-center">
          <img 
            src="https://gmmfphwrwhvixldazcmp.supabase.co/storage/v1/object/sign/images/PL%20Logo.svg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV80Y2E1MDZiYi03YzlmLTQ3NWMtODU0My1iNzkyMTc5NjdjNjAiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvUEwgTG9nby5zdmciLCJpYXQiOjE3NTI3ODIwODUsImV4cCI6NDkwNjM4MjA4NX0.r9VxAKcMh1tB91moPz4hQCm-MqVHE_0ueHoOpq3UgDU" 
            alt="Players Lockeroom Connect" 
            className="h-10 w-auto"
          />
        </Link>

        {/* Role-based navigation menu */}
        {isAuthenticated ? (
          <RoleBasedNavigationMenu 
            userRole={permissions.role}
            permissions={permissions}
          />
        ) : (
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-gray-300 hover:text-sports-green transition-colors">
              Home
            </Link>
            <div className="flex items-center space-x-2">
              <Link 
                to="/agent-beta" 
                className="bg-orange-500/20 text-orange-400 border border-orange-500/30 px-3 py-1 rounded-l-full text-xs font-semibold hover:bg-orange-500/30 hover:border-orange-500/50 transition-colors"
              >
                BETA Apply
              </Link>
              <Link 
                to="/agent/application-status" 
                className="bg-orange-500/20 text-orange-400 border border-orange-500/30 px-3 py-1 rounded-r-full text-xs font-semibold hover:bg-orange-500/30 hover:border-orange-500/50 transition-colors border-l-0"
              >
                BETA Login
              </Link>
            </div>
            <Link to="/games" className="text-gray-300 hover:text-sports-green transition-colors">
              Games
            </Link>
            <Link to="/teams-search" className="text-gray-300 hover:text-sports-green transition-colors">
              Teams
            </Link>
            <Link to="/shop" className="text-gray-300 hover:text-sports-green transition-colors flex items-center">
              Shop
              <span className="ml-2 bg-sports-green text-black text-xs px-2 py-1 rounded">Soon</span>
            </Link>
          </div>
        )}

        <div className="flex items-center space-x-4">
          {/* Show loading state while auth is initializing */}
          {!bothSystemsReady && anySystemLoading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sports-green"></div>
              <span className="text-gray-400 text-sm">Loading...</span>
            </div>
          ) : isAuthenticated ? (
            <>
              <ComprehensiveNotificationBell />
              <UserMenu />
            </>
          ) : (
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                className="text-gray-300 hover:text-black hover:bg-white"
                onClick={handleSignInClick}
              >
                Sign In
              </Button>
              <Link to="/register">
                <Button className="bg-sports-green hover:bg-sports-green/90 text-black">
                  Get Started
                </Button>
              </Link>
            </div>
          )}
        </div>
        
        {/* Workflow Automation Component */}
        {isAuthenticated && (
          <GameWorkflowAutomation 
            userId={userId}
            userRole={permissions.role}
          />
        )}
      </div>
    </header>
  );
};

export default Header;
